import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { AIRO } from "@/constants/testIds";
import { trackEvent } from "@/lib/attribution";

const rise = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export const PageHeader = ({ label, title, intro }) => (
  <header className="pt-36 md:pt-44 pb-16 md:pb-24 border-b border-[#141414]">
    <div className="max-w-6xl mx-auto px-6 md:px-12">
      <motion.div {...rise} className="mono-label text-cyan-400 mb-8">{label}</motion.div>
      <motion.h1 {...rise} className="font-heading font-extrabold tracking-tighter leading-[0.95] text-4xl md:text-6xl lg:text-7xl max-w-4xl whitespace-pre-line">
        {title}
      </motion.h1>
      {intro && (
        <motion.p {...rise} className="mt-8 max-w-2xl text-lg md:text-xl text-zinc-400 leading-relaxed">
          {intro}
        </motion.p>
      )}
    </div>
  </header>
);

export const StartCTA = ({
  eyebrow = "START YOUR PRODUCT JOURNEY",
  title = "You have the idea.\nWe will build it.",
  sub = "You don't need to know what technology your idea requires. That's our job.",
  testid = AIRO.startCta,
}) => (
  <section className="py-28 md:py-40 border-t border-[#141414]">
    <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
      <motion.div {...rise} className="mono-label text-cyan-400 mb-8">{eyebrow}</motion.div>
      <motion.h2 {...rise} className="font-heading font-extrabold tracking-tighter leading-[0.95] text-5xl md:text-7xl whitespace-pre-line">
        {title}
      </motion.h2>
      <motion.p {...rise} className="mt-8 max-w-xl mx-auto text-base md:text-lg text-zinc-400 leading-relaxed">
        {sub}
      </motion.p>
      <motion.div {...rise} className="mt-11">
        <Link
          to="/start"
          data-testid={testid}
          className="group inline-flex items-center gap-3 bg-white text-black font-medium px-9 py-5 rounded-full mono-label hover:bg-cyan-400 transition-colors duration-300"
        >
          Start Your Product Journey
          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
        </Link>
      </motion.div>
    </div>
  </section>
);

export const Eyebrow = ({ children }) => (
  <div className="mono-label text-cyan-400 mb-7">{children}</div>
);

export const StartBuild = ({ testid = "start-build-btn", size = "lg", className = "", label = "Start Your Build" }) => {
  const pad = size === "xl" ? "px-10 py-5 text-base" : size === "lg" ? "px-9 py-5 text-sm" : "px-7 py-3.5 text-sm";
  const onClick = () => { try { trackEvent("start_build_click", { label }); } catch (e) { /* noop */ } };
  return (
    <Link
      to="/start"
      onClick={onClick}
      data-testid={testid}
      className={`group inline-flex items-center gap-3 bg-white text-black font-semibold rounded-full mono-label hover:bg-cyan-400 hover:shadow-[0_0_30px_rgba(0,229,255,0.35)] transition-all duration-300 ${pad} ${className}`}
    >
      {label}
      <ArrowRight className={`${size === "xl" ? "h-5 w-5" : "h-4 w-4"} group-hover:translate-x-1 transition-transform duration-300`} />
    </Link>
  );
};

export const ScrollCTA = ({ target, children, testid, className = "" }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth" })}
    className={`group inline-flex items-center gap-2 text-zinc-300 font-medium mono-label hover:text-white transition-colors duration-300 ${className}`}
  >
    {children}
    <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform duration-300" />
  </button>
);

export const SecondaryLink = ({ to, children, testid, className = "" }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`group inline-flex items-center gap-2 border border-white/15 text-white font-medium rounded-full px-7 py-3.5 mono-label hover:border-cyan-400/50 hover:text-cyan-300 transition-colors duration-300 ${className}`}
  >
    {children}
    <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform duration-300" />
  </Link>
);
